export const meta = {
  id: '07-introduction-to-gender',
  order: 7,
  checkFocus: { cases: ['acc'] },   // what the end-of-lesson check tests first
  titleFi: 'Johdatus sukuihin',
  titleEn: 'Introduction to gender',
  duolingoUnit: 'duo-05-introduction-to-gender',
  lemmaIds: ['hūrenka', 'qogror', 'hūra', 'vēzenka', 'vēzos', 'embōñe', 'embar', 'tegōñe', 'tegon'],
  exampleIds: [
    'ex-4ce5e1942003',
    'ex-7d2a13d26ee1',
    'ex-0d663edd5072',
    'ex-efa29ad3659b',
    'ex-924c429d7a6a',
    'ex-2203afa7e0c8',
    'ex-1c6ea66c8e1e',
  ],
  sources: [
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Introduction_to_gender',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar#Gender',
    'https://wiki.languageinvention.com/index.php?title=User:Najahho/High_Valyrian_Gender',
    'https://wiki.languageinvention.com/index.php?title=User:Najahho/High_Valyrian_Noun_Declensions',
  ],
  // the week (docs/COURSE.md "Lesson hub")
  grammar: ['genders'],
  pronunciation: { focus: 'y', words: ['bony', 'kony', 'bisy', 'kesy', 'sylutegon'] },
  letter: {
    fi: 'Kirjoita viisi sanaa ja niiden suku, ja käytä kahta niistä lauseessa.',
    en: 'Write five words with their gender and use two of them in a sentence.',
  },
}

# Johdatus sukuihin

Nyt tulee asia, johon suomesta ei ole mitään apua. Korkeavalyriassa on kieliopillisia sukuja, ja niitä on neljä. Suomessa sukuja ei ole lainkaan – meillä ei ole edes eri *hän*-pronomineja naiselle ja miehelle. Siksi tämä oppitunti kannattaa lukea rauhassa ja ottaa vastaan kokonaan uutena ajatuksena.

## Suku ei ole sukupuoli

Duolingon Tips & Notes -ohjeiden mukaan korkeavalyrian suvut muistuttavat espanjan, saksan tai arabian sukuja siinä, että jokainen substantiivi kuuluu johonkin sukuun ja adjektiivit mukautuvat siihen. Toisin kuin noissa kielissä, korkeavalyrian suvuilla **ei ole mitään tekemistä biologisen sukupuolen kanssa**. Wikin kielioppi sanoo saman suoraan: suvut eivät liity sukupuoleen. Esimerkiksi <Hv glossed>vala</Hv> ’mies’ ja <Hv glossed>ābra</Hv> ’nainen’ kuuluvat samaan sukuun.

Kukin suku on nimetty tyyppisanansa mukaan, eli sanan, joka on suvulleen esikuva:

- **Kuusuku** – <Hv glossed>hūrenkon qogror</Hv>, sanasta <Hv glossed>hūra</Hv> ’kuu’
- **Aurinkosuku** – <Hv glossed>vēzenkon qogror</Hv>, sanasta <Hv glossed>vēzos</Hv> ’aurinko’
- **Vesisuku** – <Hv glossed>embōñor qogror</Hv>, sanasta <Hv glossed>embar</Hv> ’meri’
- **Maasuku** – <Hv glossed>tegōñor qogror</Hv>, sanasta <Hv glossed>tegon</Hv> ’maa’

Tällä kurssilla suvut nimetään suomeksi niiden elementin mukaan: kuu, aurinko, vesi ja maa.

<Example id="ex-4ce5e1942003" />

<Example id="ex-7d2a13d26ee1" />

<Example id="ex-0d663edd5072" />

<Example id="ex-efa29ad3659b" />

<Pronounce word="hūra" />

<Pronounce l="vēzos" word="vēzos" />

<Pronounce word="embar" />

<Pronounce word="tegon" />

## Jo sukujen nimet näyttävät, miten suku toimii

<Hv glossed>Qogror</Hv> tarkoittaa ’luokka, ryhmä, rivistö’ – ja kieliopissa myös ’suku’. Se on itse maasukuinen sana, ja se on <Term id="kollektiivi">kollektiivissa</Term>. Siksi kaikki neljä adjektiivia ovat nimissä maasuvun muodossa: <Hv>hūrenkon</Hv>, <Hv>vēzenkon</Hv>, <Hv>embōñor</Hv>, <Hv>tegōñor</Hv>. Jopa kuusuvun nimessä adjektiivi on siis maasuvun muodossa. Tämä muistuttaa hyvin siitä, että adjektiivin pääte riippuu substantiivista eikä adjektiivin merkityksestä.

<Interlinear hv="Hūrenk|on qogr|or." gloss="kuun-TERR.NOM luokka-NOM.COL" fi="Kuusuku (sanatarkasti: kuun luokka)." />

<Pronounce l="qogror" word="qogror" />

<Note>
Wikin sukusivun mukaan kuu- ja aurinkosuvun nimet kirjoitettiin pitkään muodossa *hūrenkor qogror* ja *vēzenkor qogror*. David J. Peterson on sittemmin oikaissut, että <Hv l="qogror">qogror</Hv> on maasukuinen ja nimet ovat <Hv>hūrenkon qogror</Hv> ja <Hv>vēzenkon qogror</Hv>. Duolingon ohjeissa ovat oikeat muodot. Jos törmäätte verkossa *-kor*-muotoihin, kyse on tuosta vanhasta virheestä.
</Note>

<Paradigm lemma="hūrenka" filter="positive.pre" cases="nom" numbers="sg" />

Taulukosta näkyy, miten yksi adjektiivi saa neljä eri muotoa sen mukaan, minkä suvun substantiivia se kuvaa.

## Jokainen substantiivi kuuluu yhteen sukuun

Duolingon ohjeiden mukaan jokainen korkeavalyrian substantiivi kuuluu yhteen näistä neljästä suvusta, ja sitä kuvaavien adjektiivien on mukauduttava siihen. Wikin sukusivulla on tästä selkeä esimerkki sanalla ’minun’:

- <Hv glossed>ñuha muña</Hv> ’äitini’ (kuusuku)
- <Hv glossed>ñuhys zaldrīzes</Hv> ’lohikäärmeeni’ (aurinkosuku)
- <Hv glossed>ñuhon glaeson</Hv> ’elämäni’ (maasuku)
- <Hv glossed>ñuhor hāedar</Hv> ’pikkusiskoni’ (vesisuku)

Suomen *minun* pysyy samana, mutta korkeavalyriassa sanan pääte vaihtuu substantiivin suvun mukaan.

Sukusivun mukaan monella adjektiivilla on käytännössä vain kaksi muotoa: yksi kuu- ja aurinkosuvulle ja toinen maa- ja vesisuvulle. Tuttu <Hv glossed>sȳz</Hv> ’hyvä’ on tällainen: kuu- ja aurinkosukuisen sanan kanssa se on <Hv>sȳz</Hv>, maa- ja vesisukuisen kanssa <Hv>sȳrior</Hv>. Samoin tämän osion <Hv>embōñe</Hv> ja <Hv>tegōñe</Hv>: kuu- ja aurinkosuvussa pääte on *-e*, maa- ja vesisuvussa *-or*.

## Mistä suvun tunnistaa?

Duolingon ohjeiden mukaan suvun voi useimmiten tunnistaa sille tyypillisistä päätteistä. Wikin kielioppi antaa yleisen nyrkkisäännön:

- **kuusukuiset** sanat päättyvät useimmiten vokaaliin
- **aurinkosukuiset** useimmiten *-s*:ään
- **maasukuiset** useimmiten *-n*:ään
- **vesisukuiset** useimmiten *-r*:ään

Poikkeuksia on kuitenkin paljon. Kollektiivin nominatiivi päättyy aina *-r*:ään ja muutama-luvun nominatiivi *-n*:ään suvusta riippumatta. <Hv l="qogror">Qogror</Hv> on juuri tällainen: se päättyy *-r*:ään, koska se on kollektiivi, eikä siksi, että se olisi vesisukuinen. Seuraavilla kahdella oppitunnilla käydään läpi kunkin suvun päätteet.

<Note>
Toinen samanlainen ansa: <Hv glossed>lopor</Hv> ’suola’ näyttää vesisukuiselta, mutta se on maasukuisen sanan <Hv glossed>lopon</Hv> ’suola’ kollektiivi ja siis itsekin maasukua. Damien Halmen (Academy of High Valyrian) taulukoissa se on vesisuvun esimerkkinä, joten tähän ansaan voi langeta opettajakin.
</Note>

<Paradigm lemma="hūra" cases="nom,acc" numbers="sg,pl" />

<Paradigm lemma="vēzos" cases="nom,acc" numbers="sg,pl" />

<Paradigm lemma="embar" cases="nom,acc" numbers="sg,pl" />

<Paradigm lemma="tegon" cases="nom,acc" numbers="sg,pl" />

Jo näistä neljästä taulukosta näkee, että suvut taipuvat eri tavoin. <Hv>Hūra</Hv> saa akkusatiivin <Seam s="hūr" e="e" />, kuten <LessonLink n={5}>oppitunnin 5</LessonLink> <Hv>ābra</Hv>. <Hv l="vēzos">Vēzos</Hv> ja <Hv>tegon</Hv> pysyvät yksikön akkusatiivissa ennallaan. <Hv>Embar</Hv> muuttuu muotoon <Seam s="emb" e="ri" />.

## Merkitys antaa vihjeitä

Suku ei seuraa merkitystä tarkasti, mutta wikin kieliopissa ja sukusivulla on havaittu selviä taipumuksia:

- **Kuusuku**: monet ihmistä tarkoittavat sanat (<Hv>vala</Hv>, <Hv>ābra</Hv>, <Hv glossed>muña</Hv> ’äiti’), yöeläimet (<Hv glossed>kēli</Hv> ’kissa’, <Hv glossed>atroksia</Hv> ’pöllö’) ja sotavarusteet (<Hv glossed>korze</Hv> ’pitkämiekka’, <Hv glossed>gelte</Hv> ’kypärä’).
- **Aurinkosuku**: myös ihmisiä (<Hv glossed>zentys</Hv> ’vieras’), päiväeläimet (<Hv glossed>zaldrīzes</Hv> ’lohikäärme’, <Hv glossed>hobres</Hv> ’pukki’), ammatit (<Hv>azantys</Hv>, <Hv glossed>dārys</Hv> ’kuningas’) ja ruumiinosat (<Hv glossed>deks</Hv> ’jalka’, <Hv glossed>relgos</Hv> ’suu’).
- **Vesisuku**: nesteet ja vesistöt (<Hv glossed>iēdar</Hv> ’vesi’, <Hv glossed>ānogar</Hv> ’veri’, <Hv glossed>embar</Hv> ’meri’, <Hv glossed>qelbar</Hv> ’joki’).
- **Maasuku**: ruoat, kasvit ja metallit (<Hv glossed>havon</Hv> ’leipä’, <Hv glossed>rūklon</Hv> ’kukka’, <Hv glossed>āeksion</Hv> ’kulta’).

Nämä ovat muistiapuja, eivät sääntöjä. Suku opetellaan jokaisen sanan mukana, ja sanakirja kertoo sen aina.

<Note>
**Miksi juuri kuu, aurinko, maa ja vesi?** Peterson kertoo wikissä, että suvut syntyivät kahden jaon ristiin. Ensimmäinen jako on laskettava sana vai ainesana: kuu ja aurinko ovat yksittäisiä kappaleita, vesi ja maa ainetta. Toinen jako on kaksi vartalon teemaa, A ja B. Teemasta A tuli kuu- ja vesisuku, teemasta B aurinko- ja maasuku. Kuu ja vuorovesi kuuluvat yhteen, ja aurinko antaa elämän kaikelle, mikä maasta kasvaa. Nimet hän sai arabiasta, jossa kirjaimet jaetaan aurinko- ja kuukirjaimiin. Lisäksi hän halusi useimmat ihmistä tarkoittavat sanat samaan sukuun, ja ne ovatkin kuusukua. Petersonin selityksen nosti esiin jan Misali (Conlang Critic).
</Note>

<Example id="ex-924c429d7a6a" />

<Example id="ex-2203afa7e0c8" />

<Example id="ex-1c6ea66c8e1e" />

Ensimmäisessä lauseessa sekä <Hv glossed>jelmio</Hv> ’tuuli’ että <Hv glossed>hūra</Hv> ’kuu’ ovat kuusukuisia. Toisessa aurinko paistaa järvelle: <Hv glossed>nāvri</Hv> on vesisukuisen sanan <Hv glossed>nāvar</Hv> ’järvi’ taivutusmuoto. Viimeisessä kaikki kolme ovat eri sukua: <Hv glossed>jēdar</Hv> ’taivas’ on vesisukuinen, <Hv glossed>tegon</Hv> ’maa’ maasukuinen ja <Hv glossed>perzys</Hv> ’tuli’ aurinkosukuinen. Taivas kuuluu veden sukuun ja tuli auringon – joskus suvut tuntuvat melkein runolta.

## Harjoitellaan

<Practice hv="Ābra hūre urnes." fi="Nainen näkee kuun." />

<Practice hv="Vala vēzos urnes." fi="Mies näkee auringon." />

<Practice hv="Azantys tegon urnes." fi="Ritari näkee maan." />

<Practice hv="Vali embri urnesi." fi="Miehet näkevät meren." />

Kaikissa neljässä lauseessa objekti on akkusatiivissa, mutta akkusatiivi näyttää eri suvuissa erilaiselta. Kuu muuttuu, aurinko ja maa eivät, ja meri muuttuu omalla tavallaan.

<Vocab />

<Check />
